import { useEffect, useState } from "react";
import {
  getAuth,
  onAuthStateChanged,
  updateProfile,
  verifyBeforeUpdateEmail,
  signOut,
  updatePassword,
} from "firebase/auth";

import profile from "../assets/img/signup/profile.png";
import "../assets/css/user_profile.css";
import { handleError } from "../components/common/error_method";
import ProfileMetas from "../metas/user_profile";
import { databaseKey } from "../components/common/database_key";
import {
  fetchData,
  getData,
  updateData,
} from "../components/firebase/firebase_methods";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { trackButtonClick } from "./event_track";
import { ToastContainer, toast } from "react-toastify";
import CreatableSelect from "react-select/creatable";
import TutorProfile from "../components/tutor/tutor_details";
import PrivacyPolicyModal from "../components/tutor/tutor_policy";

const UserProfile = () => {
  const [user, setUser] = useState({
    name: "",
    email: "",
    mobile: "",
    gender: "",
    skills: [],
  });
  const [password, setPassword] = useState("");
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [userRank, setUserRank] = useState("Iron");
  const [isProfilePublic, setIsProfilePublic] = useState(false);
  const [userId, setUserId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isTutor, setIsTutor] = useState(false);
  const [showPolicyModal, setShowPolicyModal] = useState(false);
  const [agreedToPolicy, setAgreedToPolicy] = useState(false);

  const collectionName = databaseKey();
  const navigate = useNavigate();

  useEffect(() => {
    const storedUserName = localStorage.getItem("userName") || "";
    setUser((prev) => ({ ...prev, name: storedUserName }));
  }, []);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        setIsAnonymous(firebaseUser.isAnonymous);
        setUserId(firebaseUser.uid);
        setUser((prev) => ({
          ...prev,
          name: firebaseUser.displayName || prev.name,
          email: firebaseUser.email || "",
        }));
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const fetchUserRank = async () => {
      try {
        const userGameRecordRef = fetchData(
          `${collectionName}/game_records/${user.name}`
        );
        const userGameRecordSnapshot = await getData(userGameRecordRef);
        if (userGameRecordSnapshot.exists()) {
          setUserRank(userGameRecordSnapshot.val().rank || "Iron");
        }
      } catch (error) {
        handleError("Error fetching user rank: " + error);
      }
    };
    if (user.name) fetchUserRank();
  }, [user.name, collectionName]);

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const userRef = fetchData(`${collectionName}/users/${userId}`);
        const userSnapshot = await getData(userRef);

        if (userSnapshot.exists()) {
          const specificUserData = userSnapshot.val();
          setUser((prev) => ({ ...prev, ...specificUserData }));
          setIsTutor(specificUserData.isTutor || false);
          setIsProfilePublic(specificUserData.isProfilePublic || false);
        }
      } catch (error) {
        handleError("Error fetching user details: " + error);
      }
    };

    if (userId) fetchUserDetails();
  }, [userId, collectionName]);

  const handleSaveClick = async (event) => {
    event.preventDefault();
    trackButtonClick("User Profile Page");
    if (!validateForm()) return;

    setIsLoading(true);
    const auth = getAuth();
    const currentUser = auth.currentUser;

    try {
      if (collectionName && userId) {
        const profileRef = fetchData(`${collectionName}/users/${userId}`);
        await updateData(profileRef, {
          ...user,
          isTutor,
          isProfilePublic,
          subjects: user.subjects || [],
          socialLinks: user.socialLinks || {
            facebook: "",
            linkedin: "",
            twitter: "",
          },
          timestamp: new Date().toString(),
        });
      }

      if (user.email !== currentUser.email) {
        await verifyBeforeUpdateEmail(currentUser, user.email);
        if (password) await updatePassword(currentUser, password);

        Swal.fire({
          title: "Email Verification",
          text: "Please check your inbox for a verification link.",
          icon: "info",
          confirmButtonText: "OK",
        }).then(() => {
          signOut(auth).then(() => navigate("/sign-up"));
        });
        return;
      }

      await updateProfile(currentUser, { displayName: user.name });
      localStorage.setItem("userName", user.name);
      toast.success("Profile updated successfully!");
      setIsLoading(false);
    } catch (error) {
      handleError(error);
      setIsLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setUser((prev) => ({ ...prev, [field]: value }));
  };

  const handleSelectChange = (field, selectedOptions) => {
    const values = selectedOptions.map((option) => option.value);
    handleChange(field, values);
  };

  const handlePublicProfileChange = () => {
    if (!agreedToPolicy) {
      setShowPolicyModal(true);
    } else {
      setIsProfilePublic(!isProfilePublic);
    }
  };

  const handleCancelPolicy = () => {
    setShowPolicyModal(false);
    setIsProfilePublic(false);
  };

  const handleAgree = () => {
    setAgreedToPolicy(true);
    setIsProfilePublic(true);
    setShowPolicyModal(false);
  };

  const validateForm = () => {
    if (!user.name.trim()) {
      toast.error("Name is required!");
      return false;
    }

    if (!user.email.trim()) {
      toast.error("Email is required!");
      return false;
    }

    if (!user.mobile.trim()) {
      toast.error("Mobile number is required!");
      return false;
    } else if (!/^\d{10}$/.test(user.mobile)) {
      toast.error("Mobile number must be exactly 10 digits!");
      return false;
    }

    if (isAnonymous) {
      if (!password.trim()) {
        toast.error("Password is required!");
        return false;
      } else if (password.length < 6) {
        toast.error("Password must be at least 6 characters long!");
        return false;
      }
    }

    const socialLinks = user.socialLinks || {};
    const socialPatterns = {
      facebook: /^https?:\/\/(www\.)?facebook\.com\/.*/i,
      linkedin: /^https?:\/\/(www\.)?linkedin\.com\/.*/i,
      twitter: /^https?:\/\/(www\.)?(twitter|x)\.com\/.*/i,
    };

    for (const [platform, pattern] of Object.entries(socialPatterns)) {
      if (socialLinks[platform] && !pattern.test(socialLinks[platform])) {
        toast.error(
          `Enter a valid ${
            platform.charAt(0).toUpperCase() + platform.slice(1)
          } URL!`
        );
        return false;
      }
    }

    return true;
  };

  return (
    <>
      <ToastContainer />
      <ProfileMetas />
      <div className="breadcrumbs" data-aos="fade-in">
        <div className="container">
          <h2>User Details</h2>
          <p>
            Manage your account details and track your progress in MathSpeedUp.
          </p>
        </div>
      </div>
      <div className="profile-container">
        <form className="profile-box" onSubmit={handleSaveClick}>
          <h2>My Profile</h2>
          <img className="profile-img" src={profile} alt="Profile" />
          <div className="profile-details">
            <div className="row">
              <div className="col-md-6 form-group">
                <input
                  type="text"
                  name="name"
                  className="form-control"
                  placeholder="Name"
                  value={user.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                />
              </div>
              <div className="col-md-6 form-group">
                <input
                  type="email"
                  name="email"
                  className="form-control"
                  placeholder="Email"
                  value={user.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  readOnly={!isAnonymous}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 form-group">
                <input
                  type="text"
                  name="mobile"
                  className="form-control"
                  placeholder="Mobile"
                  value={user.mobile}
                  onChange={(e) => handleChange("mobile", e.target.value)}
                  title="Mobile number must be exactly 10 digits"
                />
              </div>
              <div className="col-md-6 form-group">
                <CreatableSelect
                  isMulti
                  name="skills"
                  className="basic-multi-select"
                  classNamePrefix="select"
                  value={user.skills.map((skill) => ({
                    value: skill,
                    label: skill,
                  }))}
                  onChange={(selectedOptions) =>
                    handleSelectChange("skills", selectedOptions)
                  }
                  placeholder="Add skills..."
                  noOptionsMessage={() => "Start typing to add new skills"}
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-4 form-group">
                <input
                  type="text"
                  name="facebook"
                  className="form-control"
                  placeholder="Facebook Profile Link"
                  value={user.socialLinks?.facebook || ""}
                  onChange={(e) =>
                    handleChange("socialLinks", {
                      ...user.socialLinks,
                      facebook: e.target.value,
                    })
                  }
                  title="Enter a valid Facebook URL (e.g., https://www.facebook.com/username)"
                />
              </div>
              <div className="col-md-4 form-group">
                <input
                  type="text"
                  name="linkedin"
                  className="form-control"
                  placeholder="LinkedIn Profile Link"
                  value={user.socialLinks?.linkedin || ""}
                  onChange={(e) =>
                    handleChange("socialLinks", {
                      ...user.socialLinks,
                      linkedin: e.target.value,
                    })
                  }
                  title="Enter a valid LinkedIn URL (e.g., https://www.linkedin.com/in/username)"
                />
              </div>
              <div className="col-md-4 form-group">
                <input
                  type="text"
                  name="twitter"
                  className="form-control"
                  placeholder="Twitter Profile Link"
                  value={user.socialLinks?.twitter || ""}
                  onChange={(e) =>
                    handleChange("socialLinks", {
                      ...user.socialLinks,
                      twitter: e.target.value,
                    })
                  }
                  title="Enter a valid Twitter/X URL (e.g., https://twitter.com/username or https://x.com/username)"
                />
              </div>
            </div>
            {isAnonymous && (
              <div className="row">
                <div className="col-md-6 form-group">
                  <input
                    type="password"
                    name="password"
                    className="form-control"
                    placeholder="New Password"
                    onChange={(e) => setPassword(e.target.value)}
                  />
                </div>
              </div>
            )}

            <div className="row d-flex flex-column flex-md-row">
              <div className="col-md-4 form-group mb-3 mb-md-0">
                <div className="radio-group">
                  {["male", "female", "other"].map((g) => (
                    <label className="radio-label d-block" key={g}>
                      <input
                        type="radio"
                        name="gender"
                        value={g}
                        checked={user.gender === g}
                        onChange={(e) => handleChange("gender", e.target.value)}
                      />
                      &nbsp;{g.charAt(0).toUpperCase() + g.slice(1)}
                    </label>
                  ))}
                </div>
              </div>

              <div
                className="col-md-4 form-group mb-3 mb-md-0"
                style={{ display: "flex" }}
              >
                <label>
                  <input
                    type="checkbox"
                    checked={isTutor}
                    onChange={() => setIsTutor(!isTutor)}
                  />
                  &nbsp;I am a Tutor
                </label>
              </div>

              <div className="col-md-4 form-group" style={{ display: "flex" }}>
                <label>
                  <input
                    type="checkbox"
                    checked={isProfilePublic}
                    onChange={handlePublicProfileChange}
                  />
                  &nbsp;Make Profile Public
                </label>
              </div>
            </div>

            {isTutor && <TutorProfile isTutor={isTutor} userId={userId} />}

            <button type="submit" className="save-profile">
              {isLoading ? <span>Saving...</span> : "Update Profile"}
            </button>

            <div className="rank">
              <span className="user_rank">{userRank}</span>
            </div>
          </div>
        </form>
      </div>
      <PrivacyPolicyModal
        showPolicyModal={showPolicyModal}
        setShowPolicyModal={setShowPolicyModal}
        handleAgree={handleAgree}
        handleCancel={handleCancelPolicy}
      />
    </>
  );
};

export default UserProfile;
